/** Session-persisted search/filter state for SUMMARY_LIST DataTables (clears when tab closes). */

export type SummarySearchSession = {
  filters: Record<string, unknown>;
  sortConfig?: { key: string; direction: "asc" | "desc" } | null;
  selectedSearchField?: string;
};

const SESSION_PREFIX = "garage360_summary_search_";

const sanitizeKeyPart = (value: string): string =>
  value.replace(/[^a-zA-Z0-9_-]/g, "_");

export function buildSummarySearchSessionKey(parts: {
  moduleName: string;
  branchId?: string;
  scope?: string;
}): string {
  const modulePart = sanitizeKeyPart(parts.moduleName || "unknown");
  const branchPart = parts.branchId
    ? `_b_${sanitizeKeyPart(parts.branchId)}`
    : "";
  const scopePart = parts.scope ? `_s_${sanitizeKeyPart(parts.scope)}` : "";
  return `${SESSION_PREFIX}${modulePart}${branchPart}${scopePart}`;
}

const hasMeaningfulFilters = (filters: Record<string, unknown>): boolean =>
  Object.values(filters).some((value) => {
    if (value === undefined || value === null || value === "") return false;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  });

export function loadSummarySearchSession(
  key: string
): SummarySearchSession | null {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined") {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SummarySearchSession;
    if (!parsed || typeof parsed !== "object" || !parsed.filters) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveSummarySearchSession(
  key: string,
  data: SummarySearchSession
): void {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined") {
    return;
  }
  const filters = data.filters || {};
  const hasFilters = hasMeaningfulFilters(filters);
  const hasSort = !!data.sortConfig;
  const hasSearchField = !!data.selectedSearchField;

  if (!hasFilters && !hasSort && !hasSearchField) {
    sessionStorage.removeItem(key);
    return;
  }

  try {
    sessionStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.warn("[SummarySearchSession] Failed to save session:", error);
  }
}

export function clearSummarySearchSession(key: string): void {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined") {
    return;
  }
  sessionStorage.removeItem(key);
}

export function readInitialSummarySearchSession(
  config: { apiname?: string; Name?: string; Form?: { Name?: string } },
  options?: { branchId?: string; scope?: string }
): SummarySearchSession | null {
  const moduleName =
    config.apiname || config.Form?.Name || config.Name || "";
  if (!moduleName) return null;
  const key = buildSummarySearchSessionKey({
    moduleName,
    branchId: options?.branchId,
    scope: options?.scope,
  });
  return loadSummarySearchSession(key);
}
